import { User } from "oidc-client-ts";
import { doGet, doPost, doPut, doDelete } from "./util/apiHelper.ts";
import {
  useAuthenticatedMutation,
  useAuthenticatedQuery,
} from "./util/queryHelper.ts";
import {
  APPOINTMENT_QUERY_KEYS,
  BOOKING_INVALIDATION_KEYS,
  BOOKING_QUERY_KEYS,
} from "./util/queryKeys.ts";

const CHANGE_REQUEST_INVALIDATION_KEYS: unknown[][] = [
  BOOKING_INVALIDATION_KEYS.changeRequests,
  BOOKING_INVALIDATION_KEYS.userBooking,
  BOOKING_QUERY_KEYS.userBookings(),
  BOOKING_QUERY_KEYS.teacherBookings(),
  BOOKING_INVALIDATION_KEYS.userPendingChangeRequests,
  APPOINTMENT_QUERY_KEYS.scholarAppointments(),
  APPOINTMENT_QUERY_KEYS.teacherAppointments(),
];

// --- Types ---

export type BookingChangeRequestStatus =
  | "PENDING"
  | "ACCEPTED"
  | "REJECTED"
  | "SUPERSEDED"
  | "CANCELLED";

export interface TimeslotChangeDto {
  originalTimeslotId: string;
  proposedFrom: string; // ISO instant string
  proposedTo: string;
}

export interface BookingChangeRequestCreationDto {
  timeslotChanges: TimeslotChangeDto[];
  message?: string;
}

export interface BookingChangeRequestUpdateDto {
  status: BookingChangeRequestStatus;
  responseMessage?: string;
}

export interface TimeslotChangeResponseDto {
  originalTimeslotId: string;
  originalFrom: string;
  originalTo: string;
  proposedFrom: string;
  proposedTo: string;
}

export interface BookingChangeRequestResponseDto {
  id: string;
  bookingId: string;
  requestedBy: string;
  requestedByName: string;
  instrument: string;
  status: BookingChangeRequestStatus;
  message: string | null;
  responseMessage: string | null;
  respondedAt: string | null;
  parentRequestId: string | null;
  timeslotChanges: TimeslotChangeResponseDto[];
  createdAt: string;
  /** When a still-pending request will be auto-cancelled; null once it is no longer pending. */
  autoCancelAt: string | null;
}

export interface BookingChangeRequestListDto {
  changeRequests: BookingChangeRequestResponseDto[];
}

// --- API Functions ---

async function getChangeRequests(
  user: User,
  bookingId: string,
  status?: BookingChangeRequestStatus,
): Promise<BookingChangeRequestResponseDto[]> {
  const params: Record<string, string> = {};
  if (status) {
    params.status = status;
  }
  const response = await doGet<BookingChangeRequestListDto>(
    `/users/${user.profile.sub}/bookings/${bookingId}/change-requests`,
    user,
    params,
  );
  return response.changeRequests;
}

async function getUserPendingChangeRequests(
  user: User,
  status: BookingChangeRequestStatus = "PENDING",
): Promise<BookingChangeRequestResponseDto[]> {
  const response = await doGet<BookingChangeRequestListDto>(
    `/users/${user.profile.sub}/bookings/change-requests?status=${status}`,
    user,
  );
  return response.changeRequests;
}

async function createChangeRequest(
  user: User,
  bookingId: string,
  dto: BookingChangeRequestCreationDto,
): Promise<BookingChangeRequestResponseDto> {
  return await doPost<BookingChangeRequestResponseDto>(
    `/users/${user.profile.sub}/bookings/${bookingId}/change-requests`,
    user,
    dto,
  );
}

async function updateChangeRequest(
  user: User,
  bookingId: string,
  changeRequestId: string,
  dto: BookingChangeRequestUpdateDto,
): Promise<BookingChangeRequestResponseDto> {
  return await doPut<BookingChangeRequestResponseDto>(
    `/users/${user.profile.sub}/bookings/${bookingId}/change-requests/${changeRequestId}`,
    user,
    dto,
  );
}

async function counterProposeChangeRequest(
  user: User,
  bookingId: string,
  changeRequestId: string,
  dto: BookingChangeRequestCreationDto,
): Promise<BookingChangeRequestResponseDto> {
  return await doPost<BookingChangeRequestResponseDto>(
    `/users/${user.profile.sub}/bookings/${bookingId}/change-requests/${changeRequestId}/counter`,
    user,
    dto,
  );
}

async function cancelChangeRequest(
  user: User,
  bookingId: string,
  changeRequestId: string,
): Promise<void> {
  return await doDelete<void>(
    `/users/${user.profile.sub}/bookings/${bookingId}/change-requests/${changeRequestId}`,
    user,
  );
}

// --- Hooks ---

export function useBookingChangeRequestsQuery(
  bookingId?: string,
  status?: BookingChangeRequestStatus,
) {
  return useAuthenticatedQuery(
    BOOKING_QUERY_KEYS.changeRequests(bookingId, status),
    (user) => getChangeRequests(user, bookingId!, status),
    {
      enabled: !!bookingId,
    },
  );
}

export function usePendingChangeRequestQuery(bookingId?: string) {
  return useAuthenticatedQuery(
    BOOKING_QUERY_KEYS.changeRequests(bookingId, "PENDING"),
    async (user) => {
      const requests = await getChangeRequests(user, bookingId!, "PENDING");
      return requests.length > 0 ? requests[0] : null;
    },
    {
      enabled: !!bookingId,
    },
  );
}

export function useUserPendingChangeRequestsQuery() {
  return useAuthenticatedQuery(
    BOOKING_INVALIDATION_KEYS.userPendingChangeRequests,
    (user) => getUserPendingChangeRequests(user),
    {
      initialData: [],
    },
  );
}

export interface CreateChangeRequestVariables {
  bookingId: string;
  dto: BookingChangeRequestCreationDto;
}

export function useCreateChangeRequestMutation() {
  return useAuthenticatedMutation(
    (variables: CreateChangeRequestVariables, user) =>
      createChangeRequest(user, variables.bookingId, variables.dto),
    { invalidateKeys: CHANGE_REQUEST_INVALIDATION_KEYS },
  );
}

export interface UpdateChangeRequestVariables {
  bookingId: string;
  changeRequestId: string;
  dto: BookingChangeRequestUpdateDto;
}

export function useUpdateChangeRequestMutation() {
  return useAuthenticatedMutation(
    (variables: UpdateChangeRequestVariables, authUser) =>
      updateChangeRequest(
        authUser,
        variables.bookingId,
        variables.changeRequestId,
        variables.dto,
      ),
    { invalidateKeys: CHANGE_REQUEST_INVALIDATION_KEYS },
  );
}

export interface CounterProposeVariables {
  bookingId: string;
  changeRequestId: string;
  dto: BookingChangeRequestCreationDto;
}

export function useCounterProposeChangeRequestMutation() {
  return useAuthenticatedMutation(
    (variables: CounterProposeVariables, user) =>
      counterProposeChangeRequest(
        user,
        variables.bookingId,
        variables.changeRequestId,
        variables.dto,
      ),
    { invalidateKeys: CHANGE_REQUEST_INVALIDATION_KEYS },
  );
}

export interface CancelChangeRequestVariables {
  bookingId: string;
  changeRequestId: string;
}

export function useCancelChangeRequestMutation() {
  return useAuthenticatedMutation(
    (variables: CancelChangeRequestVariables, user) =>
      cancelChangeRequest(user, variables.bookingId, variables.changeRequestId),
    { invalidateKeys: CHANGE_REQUEST_INVALIDATION_KEYS },
  );
}
